/** @format */
import { ChangeDetectionStrategy, Component, computed, Inject, input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FudisSelectOption } from '@funidata/ngx-fudis/lib/types/forms';
import { TranslocoService } from '@ngneat/transloco';
import { PlanValidationResult, ValidatablePlan } from 'common-typescript';
import {
    AnyModuleRule,
    ContentFilter,
    EntityWithRule,
    ModuleResultItem,
    OtmId,
    SearchValidity,
    StudyModule,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppErrorHandler } from '../../../error-handler/app-error-handler';
import { CreditRangePipe } from '../../../number/credit-range.pipe';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../../plan/plan-actions-service/plan-actions.service';
import { ModuleEntityService, ModuleType } from '../../../service/module-entity.service';
import { PlanData, PlanStateObject } from '../../../service/plan-state.service';

export interface ModuleSearchOptionType {
    value: ModuleResultItem;
    label: string;
    subLabel?: string;
}

export interface ParentModule extends EntityWithRule {
    contentFilter?: ContentFilter;
}

@Component({
    selector: 'sis-plan-structure-any-module-rule',
    templateUrl: './plan-structure-any-module-rule.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureAnyModuleRuleComponent implements OnDestroy, OnInit {
    curriculumPeriodIds = input.required<OtmId[]>();
    currentUniversityOrgId = input.required<OtmId>();
    rule = input.required<AnyModuleRule>();
    parentModule = input.required<EntityWithRule>();
    planValidationResult = input.required<PlanValidationResult>();
    planStateObject = input<PlanStateObject>();
    planData = input<PlanData>();
    validatablePlan = input<ValidatablePlan>();
    ruleDepthLevel = input.required<number>();
    headingLevel = input.required<number>();
    selectionUIState = input<'ACTIVE' | 'DISABLED' | 'SELECTABLE'>('ACTIVE');

    moduleSelectControl = new FormControl<null | FudisSelectOption<ModuleSearchOptionType>>(null);
    MINOR_STUDY_RIGHT_SELECTION_TYPE_URN = 'urn:code:study-right-selection-type:minor-study-right';

    private destroy$ = new Subject<void>();
    protected searchTextUpdateSubject = new Subject<string | null>();
    protected searchResults = new BehaviorSubject<FudisSelectOption<ModuleSearchOptionType>[]>([]);
    protected studyModules: StudyModule[] = [];

    ruleValidationResult = computed(this.ruleValidationResultComputation());

    constructor(
        private appErrorHandler: AppErrorHandler,
        private moduleEntityService: ModuleEntityService,
        private creditRangePipe: CreditRangePipe,
        private translocoService: TranslocoService,
        @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService,
    ) {
        this.searchTextUpdateSubject
            .pipe(
                tap(() => this.searchResults?.next([])),
                filter(textQuery => textQuery?.trim() !== ''),
                switchMap(textQuery => this.searchModules(textQuery)),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    ngOnInit() {
        const selectedModules: StudyModule[] = _.values(this.ruleValidationResult().selectedModulesById);
        if (selectedModules) {
            this.studyModules.push(...selectedModules);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isModuleAlreadySelected(module: ModuleResultItem): boolean {
        return !!_.find(this.studyModules, { groupId: module.groupId });
    }

    searchModules(textQuery: string): Observable<ModuleSearchOptionType[]> {
        const searchParameters = {
            fullTextQuery: textQuery,
            orgRootId: this.currentUniversityOrgId(),
            curriculumPeriodIds: this.curriculumPeriodIds(),
            validity: 'ONGOING_AND_FUTURE' as SearchValidity,
            codeUrns:
                _.get(this.parentModule(), 'contentFilter.studyRightSelectionType') === this.MINOR_STUDY_RIGHT_SELECTION_TYPE_URN
                    ? [this.MINOR_STUDY_RIGHT_SELECTION_TYPE_URN]
                    : [],
            limit: 100,
            start: 0,
            type: ModuleType.STUDY_MODULE,
        };

        return this.moduleEntityService.searchUnauthenticated(searchParameters).pipe(
            map(modules =>
                modules.searchResults.map(
                    module =>
                        ({
                            value: module,
                            label: `${module.name} (${this.creditRangePipe.transform(module.credits)})`,
                            subLabel: `${module.code} | ${this.translocoService.translate('SIS_COMPONENTS.STUDY.STUDY_MODULE')}`,
                            disabled: this.isModuleAlreadySelected(module),
                        }) as FudisSelectOption<ModuleResultItem>,
                ),
            ),
            tap(opts => {
                this.searchResults?.next(opts);
            }),
            this.appErrorHandler.defaultErrorHandler(),
        );
    }

    updateSelected(option: FudisSelectOption<ModuleSearchOptionType>): void {
        if (option) {
            this.studyModules?.push(option?.value);
            this.planActionsService?.selectModuleByGroupId?.(option.value.groupId, this.parentModule());
        }
    }

    handleStudyModuleSelectToggle(studyModule: StudyModule): void {
        const parentModule = this.parentModule();
        if (!this.validatablePlan().getModuleInPlanByGroupId(studyModule.groupId)) {
            this.planActionsService?.selectModuleByGroupId?.(studyModule.groupId, parentModule);
        } else {
            this.planActionsService?.removeModule?.(studyModule, parentModule);
        }
    }

    ruleValidationResultComputation(): () => any {
        return () => _.get(this.planValidationResult().ruleValidationResults, [this.parentModule().id, this.rule().localId]);
    }
}
